import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { useMediaQuery } from "@/utils/hooks";
import {
  ComponentPropsWithoutRef,
  ElementRef,
  HTMLAttributes,
  forwardRef,
  useContext,
  useState,
} from "react";
import { ResponsiveSheetContext } from "../helpers/responsive-sheet";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerProps,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/utils/tailwind";

export const ResponsiveSheetPortal = DrawerPortal;
export const ResponsiveSheetTrigger = DrawerTrigger;
export const ResponsiveSheetOverlay = DrawerOverlay;
export const ResponsiveSheetClose = DrawerClose;
export const ResponsiveSheetTitle = DrawerTitle;
export const ResponsiveSheetDescription = DrawerDescription;
export const ResponsiveSheetHeader = DrawerHeader;
export const ResponsiveSheetFooter = DrawerFooter;

export type ResponsiveSheetProps = DrawerProps & { defaultOpen?: boolean };

export const ResponsiveSheet = ({
  children,
  open,
  onOpenChange,
  defaultOpen = false,
  ...props
}: ResponsiveSheetProps) => {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    if (!isControlled) setInternalOpen(open);
    onOpenChange?.(open);
  };

  const isSidePanel = useMediaQuery(DESKTOP_MEDIA_QUERY);
  return (
    <ResponsiveSheetContext.Provider
      value={{ isSidePanel, setOpen: handleOpenChange }}
    >
      <Drawer
        open={isControlled ? open : internalOpen}
        onOpenChange={handleOpenChange}
        direction={isSidePanel ? "right" : "bottom"}
        {...props}
      >
        {children}
      </Drawer>
    </ResponsiveSheetContext.Provider>
  );
};

const ResponsiveSheetSidePanel = forwardRef<
  ElementRef<typeof DrawerContent>,
  ComponentPropsWithoutRef<typeof DrawerContent>
>(({ className, style, children, ...props }, ref) => {
  return (
    <ResponsiveSheetPortal>
      <ResponsiveSheetOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          "right-4 top-4 bottom-4 z-50 fixed outline-hidden w-full md:w-8/12 lg:w-4/12 flex",
          className,
        )}
        style={{
          ...({
            "--initial-transform": "calc(100% + 8px)",
          } as React.CSSProperties),
          ...style,
        }}
        {...props}
      >
        <div className="bg-white w-full flex flex-col rounded-xl">
          {children}
        </div>
      </DrawerPrimitive.Content>
    </ResponsiveSheetPortal>
  );
});

export const ResponsiveSheetContent = forwardRef<
  ElementRef<typeof DrawerContent>,
  ComponentPropsWithoutRef<typeof DrawerContent>
>(({ className, ...props }, ref) => {
  const { isSidePanel } = useContext(ResponsiveSheetContext);
  const Comp = isSidePanel ? ResponsiveSheetSidePanel : DrawerContent;
  return (
    <Comp
      className={cn("lg:h-auto sm:h-auto h-5/6 lg:w-5/12 w-auto", className)}
      {...props}
      ref={ref}
    />
  );
});

export const ResponsiveSheetBody = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 px-6 pt-4 pb-6 overflow-y-auto",
        className,
      )}
      {...props}
      ref={ref}
    />
  );
});
